/**
 * Two clocks, and every instant labelled with the one it is on.
 *
 * §7.5 is one rule applied twice: show the clock the reader is reasoning in.
 *
 * - **A platform record** — a tenant's Created, a key's Created — is rendered in the
 *   **platform owner's own** zone. The owner reasoning about *when they retired a tenant*
 *   is reasoning in their own day; rendering two of them in two different tenants' zones
 *   would make them look out of order.
 * - **A tenant's own record** — when a superadmin's link expires — is rendered in **that
 *   tenant's** configured zone. The owner reading a link's expiry is about to say it out
 *   loud to somebody standing in that tenant's building, and "this works until 17:40, your
 *   time" is the only useful form of that sentence.
 *
 * **Every instant is labelled with the zone it is in**, always, because this surface mixes
 * two. A bare time here is exactly the ambiguity `docs/reference/ISSUES.md` **CF5** is
 * about.
 */

/** The zone this browser is in — the platform owner's own day. */
export function ownerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * An instant, formatted in a zone, **without** the zone named.
 *
 * For a place that names the zone itself, beside the time and in its own element — the
 * tenant header sets the IANA name in monospace after the time, where `formatInstant`'s
 * parenthesised label would say it twice. Every other caller wants {@link formatInstant}: a
 * bare time on this surface is the ambiguity **CF5** is about, so this one is only for a
 * caller that prints the label next to it.
 *
 * `Intl` does the formatting, so the owner reads the format their own machine uses. An
 * unparseable instant is a bug somewhere upstream, and it must read as a gap rather than as
 * `Invalid Date` — which is what a screen renders if nobody checks.
 */
export function formatInstantWithoutZone(iso: string | null | undefined, timeZone: string): string {
  if (iso === null || iso === undefined || iso === '') {
    return '—'
  }
  const value = new Date(iso)
  if (Number.isNaN(value.getTime())) {
    return '—'
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(value)
}

/**
 * An instant, formatted in a zone, with the zone named.
 *
 * The zone is appended as its IANA name rather than as an abbreviation: `CET` is ambiguous
 * twice a year, and the console shows the unabbreviated name everywhere else too (§7.1).
 */
export function formatInstant(iso: string | null | undefined, timeZone: string): string {
  const formatted = formatInstantWithoutZone(iso, timeZone)
  return formatted === '—' ? formatted : `${formatted} (${timeZone})`
}

/** A date with no time, in the owner's zone — the tenant list's Created column. */
export function formatDate(iso: string | null | undefined, timeZone: string): string {
  if (iso === null || iso === undefined || iso === '') {
    return '—'
  }
  const value = new Date(iso)
  if (Number.isNaN(value.getTime())) {
    return '—'
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone }).format(value)
}
